import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
    getMerchantsApi,
    getMerchantCabinetsApi,
    getMerchantsByCabinetApi,
    merchantCabinetCreateApi,
    merchantCabinetUpdateApi,
    merchantUpdateApi,
} from "@src/api";
import type {
    ErrorResponseType,
    ListDataType,
    MerchantCabinetCreateType,
    MerchantCabinetType,
    MerchantCabinetUpdateType,
    MerchantCabinetWithMerchantsType,
    MerchantCoverType,
    MerchantDetailType,
    MerchantUpdateType,
    ResponseWithMessage,
} from "@src/types";
import type { AxiosError } from "axios";
import { useAppMessage } from "@repo/ui";

export function useMerchants() {
    return useQuery<ListDataType<MerchantCoverType>, AxiosError<ErrorResponseType>>({
        queryKey: ["user-merchants"],
        queryFn: ({ signal }) => getMerchantsApi(signal),
        refetchOnWindowFocus: false,
    });
}

export function useMerchantCabinets(enabled: boolean) {
    return useQuery<ListDataType<MerchantCabinetType>, AxiosError<ErrorResponseType>>({
        queryKey: ["user-merchant-cabinets"],
        queryFn: ({ signal }) => getMerchantCabinetsApi(signal),
        refetchOnWindowFocus: false,
        enabled,
        retry: 2,
    });
}

export function useDetailedMerchants(enabled: boolean, merhcantCabinetId?: string) {
    return useQuery<ListDataType<MerchantDetailType>, AxiosError<ErrorResponseType>>({
        queryKey: ["user-detailed-merchants", merhcantCabinetId ?? "all"],
        queryFn: ({ signal }) =>
            getMerchantsByCabinetApi({ merchant_cabinet_id: merhcantCabinetId }, signal),
        enabled,
        refetchOnWindowFocus: false,
        retry: 2,
    });
}

export function useAllMerchants(enabled: boolean = true) {
    const cabinetsQ = useMerchantCabinets(enabled);
    const cabinets = cabinetsQ.data?.data ?? [];

    const canFetchDetails = cabinetsQ.status === "success" && cabinets.length > 0;
    const detailsQ = useDetailedMerchants(canFetchDetails);

    // Группируем мерчантов по cabinet_id
    const merchantsByCabinetId = useMemo(() => {
        const map = new Map<string, MerchantDetailType[]>();
        const list = detailsQ.data?.data ?? [];
        for (const m of list) {
            const key = m.merchant_cabinet_id; // оставляем твоё поле
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(m);
        }
        return map;
    }, [detailsQ.data]);

    // кабинет + его merchants
    const data: MerchantCabinetWithMerchantsType[] = useMemo(() => {
        if (!cabinets.length) return [];
        return cabinets.map((cab) => ({
            ...cab,
            merchants: merchantsByCabinetId.get(cab.id) ?? [],
        }));
    }, [cabinets, merchantsByCabinetId]);

    const isPending =
        cabinetsQ.isPending || (canFetchDetails && detailsQ.isPending && !detailsQ.data);

    const isFetching = cabinetsQ.isFetching || (canFetchDetails && detailsQ.isFetching);

    // const refetch = async () => {
    //     await Promise.all([
    //         cabinetsQ.refetch(),
    //         canFetchDetails ? detailsQ.refetch() : Promise.resolve(),
    //     ]);
    // };

    return {
        data,
        cabinets,
        isPending,
        isFetching,
        isError: cabinetsQ.isError,
        error: cabinetsQ.error,
        // refetch,
    } as const;
}

export function useMerchantCabinetCreate() {
    const message = useAppMessage();
    const queryClient = useQueryClient();

    return useMutation<
        ResponseWithMessage,
        AxiosError<ErrorResponseType>,
        MerchantCabinetCreateType
    >({
        mutationKey: ["merchant-cabinet", "create"],
        mutationFn: (payload) => merchantCabinetCreateApi(payload),

        onSuccess(data) {
            message.success(data.detail ?? "Кабинет успешно добавлен!");
        },

        onError(err) {
            message.error(err.response?.data?.detail ?? "Произошла непредвиденная ошибка!");
        },

        onSettled: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["user-merchant-cabinets"] }),
                queryClient.invalidateQueries({ queryKey: ["user-detailed-merchants", "all"] }),
            ]);
        },
    });
}

export function useMerchantCabinetUpdate(pk: string) {
    const message = useAppMessage();
    const queryClient = useQueryClient();

    return useMutation<
        MerchantCabinetType,
        AxiosError<ErrorResponseType>,
        MerchantCabinetUpdateType
    >({
        mutationKey: ["merchant-cabinet", "update", pk],
        mutationFn: (payload) => merchantCabinetUpdateApi(pk, payload),

        onSuccess(data) {
            message.success(`${data.username}: Обновлено`);
            queryClient.invalidateQueries({ queryKey: ["user-merchant-cabinets"] });
        },

        onError(err) {
            message.error(err.response?.data?.detail ?? "Произошла непредвиденная ошибка!");
        },
    });
}

export function useMerchantUpdate(pk: string) {
    const message = useAppMessage();
    const queryClient = useQueryClient();

    return useMutation<MerchantDetailType, AxiosError<ErrorResponseType>, MerchantUpdateType>({
        mutationKey: ["merchant", "update", pk],
        mutationFn: (payload) => merchantUpdateApi(pk, payload),

        onSuccess() {
            message.success("Токен обновлено");
            queryClient.invalidateQueries({ queryKey: ["user-detailed-merchants"] });
        },

        onError(err) {
            message.error(err.response?.data?.detail ?? "Произошла непредвиденная ошибка!");
        },
    });
}
